<script setup lang="ts">
import { computed, markRaw, nextTick, onMounted, ref, shallowRef } from 'vue';
import type Quill from 'quill';
import type { QuillyEditor as QuillyEditorComponent } from 'vue-quilly';

type QuillConstructor = typeof Quill;

const model = defineModel<string>({ required: true, default: () => '' });

const props = defineProps<{
  id?: string;
  placeholder?: string;
  readonly?: boolean;
  disabled?: boolean;
  error?: string;
}>();

const isReadOnly = computed(() => props.readonly || props.disabled);

const options = ref({
  theme: 'snow',
  modules: {
    toolbar: [['bold', 'italic', 'underline', 'strike'], ['link', 'emoji'], ['clean']],
    emoji: {
      autoFocus: true,
      perLine: 6,
    },
  },
  placeholder: props.placeholder,
  readOnly: isReadOnly.value,
});

const QuillyEditor = shallowRef<typeof QuillyEditorComponent>();
const editor = ref<InstanceType<typeof QuillyEditorComponent>>();
const quillInstance = ref<Quill | null>(null);

onMounted(async () => {
  const [{ default: QuillClass }, { EmojiModule }, { QuillyEditor: LoadedQuillyEditor }] = await Promise.all([
    import('quill'),
    import('@rytrox/quill-emoji-mart'),
    import('vue-quilly'),
    import('quill/dist/quill.core.css'),
    import('quill/dist/quill.snow.css'),
  ]);

  QuillClass.register('modules/emoji', EmojiModule, true);
  QuillyEditor.value = markRaw(LoadedQuillyEditor);

  await nextTick();

  quillInstance.value = editor.value?.initialize(QuillClass as QuillConstructor) ?? null;
  updateAccessibilityAttributes();
});

function updateAccessibilityAttributes(): void {
  const quillEditorElement = quillInstance.value?.root;
  if (quillEditorElement === undefined) {
    return;
  }

  quillEditorElement.setAttribute('role', 'textbox');
  quillEditorElement.setAttribute('aria-multiline', 'true');
}
</script>

<template>
  <div
    class="rich-text-editor w-full"
    :class="{
      'rich-text-editor--error': props.error,
      'rich-text-editor--readonly': props.readonly,
      'rich-text-editor--disabled': props.disabled,
    }"
  >
    <component :is="QuillyEditor" v-if="QuillyEditor" ref="editor" v-model="model" :options="options" />
  </div>
</template>

<style scoped>
.rich-text-editor {
  --rich-text-border-color: var(--app-grey-light);
  --rich-text-background-color: var(--app-white);
  --rich-text-text-color: var(--app-black);
  --rich-text-placeholder-color: var(--app-grey);
  --rich-text-toolbar-background-color: var(--app-white);
  --rich-text-toolbar-hover-background-color: var(--app-grey-ultraLight);
  --rich-text-toolbar-active-color: var(--app-primary-dark);
}

.rich-text-editor:focus-within {
  --rich-text-border-color: var(--app-black);
}

.rich-text-editor--error:not(:focus-within) {
  --rich-text-border-color: var(--app-red-dark);
  --rich-text-text-color: var(--app-red-dark);
}

.rich-text-editor--disabled {
  --rich-text-border-color: var(--app-grey-light);
  --rich-text-background-color: var(--app-grey-light);
  --rich-text-text-color: var(--app-grey);
  --rich-text-toolbar-background-color: var(--app-grey-light);
}

.rich-text-editor :deep(.ql-toolbar) {
  border-color: var(--rich-text-border-color);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  background-color: var(--rich-text-toolbar-background-color);
  transition:
    background-color 250ms,
    border-color 250ms;
}

.rich-text-editor :deep(.ql-container) {
  min-height: 180px;
  border-color: var(--rich-text-border-color);
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: var(--rich-text-background-color);
  font-family: inherit;
  transition:
    background-color 250ms,
    border-color 250ms;
}

.rich-text-editor :deep(.ql-editor) {
  min-height: 180px;
  color: var(--rich-text-text-color);
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
}

.rich-text-editor :deep(.ql-editor:focus) {
  outline: none;
}

.rich-text-editor:focus-within :deep(.ql-toolbar),
.rich-text-editor:focus-within :deep(.ql-container) {
  border-color: var(--app-black);
}

.rich-text-editor :deep(.ql-editor.ql-blank::before) {
  color: var(--rich-text-placeholder-color);
  font-style: normal;
}

.rich-text-editor :deep(.ql-editor strong),
.rich-text-editor :deep(.ql-editor b) {
  font-weight: 700;
}

.rich-text-editor :deep(.ql-snow.ql-toolbar button),
.rich-text-editor :deep(.ql-snow .ql-toolbar button) {
  border-radius: 2px;
}

.rich-text-editor :deep(.ql-snow.ql-toolbar button:hover),
.rich-text-editor :deep(.ql-snow .ql-toolbar button:hover),
.rich-text-editor :deep(.ql-snow.ql-toolbar button:focus),
.rich-text-editor :deep(.ql-snow .ql-toolbar button:focus),
.rich-text-editor :deep(.ql-snow.ql-toolbar .ql-picker-label:hover),
.rich-text-editor :deep(.ql-snow .ql-toolbar .ql-picker-label:hover) {
  background-color: var(--rich-text-toolbar-hover-background-color);
  color: var(--app-black);
}

.rich-text-editor :deep(.ql-snow.ql-toolbar button.ql-active),
.rich-text-editor :deep(.ql-snow .ql-toolbar button.ql-active),
.rich-text-editor :deep(.ql-snow.ql-toolbar .ql-picker-label.ql-active),
.rich-text-editor :deep(.ql-snow .ql-toolbar .ql-picker-label.ql-active) {
  color: var(--rich-text-toolbar-active-color);
}

.rich-text-editor--readonly :deep(.ql-toolbar),
.rich-text-editor--disabled :deep(.ql-toolbar) {
  pointer-events: none;
}

.rich-text-editor--disabled :deep(.ql-editor) {
  cursor: not-allowed;
}

:global(em-emoji-picker) {
  z-index: 9999 !important;
}
</style>
